import {
  ReactNode,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { isDesktop } from "react-device-detect";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DotWave } from "@uiball/loaders";
import MarkitHeaderLogo from "../../components/MarkitHeaderLogo";
import RectangleButton from "../../components/Buttons/RectangleButton";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import {
  LoginState,
  accountActions,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { NavigationId } from "../../navigation/AppParamList";
import { Colors } from "../../utils/colors";
import { firebaseAuth, onAuthStateChanged } from "../../firebase";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  OAuthAuthorizationRequestInfo,
  OAuthDecision,
} from "@markit/common.types";
import {
  fetchAuthorizationRequest,
  isSafeRedirectUrl,
  submitConsentDecision,
} from "./oauthApi";

/**
 * Consent screen for Markit's OAuth 2.1 authorization server.
 *
 * The backend's /oauth/authorize validates the incoming request from an AI
 * client, parks it, and 302s the browser here with the request's id. This
 * screen confirms who is signed in, shows what is being asked for, and sends
 * the decision back. The backend replies with the client's own callback URL,
 * which we navigate to for real since it leaves markitai.com.
 */

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; request: OAuthAuthorizationRequestInfo }
  | { kind: "expired" }
  | { kind: "error"; message: string };

const ACCOUNT_MISMATCH_MESSAGE =
  "You're signed in to a different Markit account in another tab. Refresh this page and try again.";

// A framed consent page could be overlaid by another site to trick a click on
// Approve. Cross-origin access to window.top can throw, which also means framed.
const isInFrame = () => {
  try {
    return window.top !== window.self;
  } catch {
    return true;
  }
};

const PageShell = (props: { children: ReactNode }) => (
  <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}>
    <Helmet>
      <title>Connect an App | Markit AI</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <div style={{ position: "absolute", top: 24, left: 24 }}>
      <MarkitHeaderLogo fullLogo alternateSize={24} />
    </div>
    <div
      className="ColumnCenter"
      style={{
        minHeight: "100vh",
        boxSizing: "border-box",
        justifyContent: "center",
        alignItems: "center",
        paddingInline: 14,
        // Keeps a tall card clear of the absolutely positioned logo on short screens.
        paddingTop: 72,
        paddingBottom: 24,
      }}
    >
      {props.children}
    </div>
  </div>
);

const CenteredMessage = (props: {
  title: string;
  subtitle: string;
  isError?: boolean;
}) => (
  <div
    className="ColumnNormal"
    style={{ gap: 14, maxWidth: 440, textAlign: "center" }}
  >
    <span
      className="bodyMedium"
      style={{ color: props.isError ? Colors.RED3 : Colors.BLUE5 }}
    >
      {props.isError ? "Something went wrong" : "Connection request"}
    </span>
    <span style={{ fontSize: 24, fontWeight: 500 }}>{props.title}</span>
    <span className="bodySubtext">{props.subtitle}</span>
  </div>
);

const OAuthConsentScreen = memo(function OAuthConsentScreenFn() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const mixpanel = useContext(MixpanelContext);

  const { account } = useSelector(getAccountState);
  const { accountData, teamMemberAccountData, originalAccountAdmin, loggedIn } =
    account;

  const requestId = searchParams.get("requestId") ?? "";

  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [pendingDecision, setPendingDecision] = useState<OAuthDecision | null>(
    null
  );
  const [submitError, setSubmitError] = useState("");
  const submittingRef = useRef(false);

  const isFramed = useMemo(isInFrame, []);

  // undefined until Firebase restores (or fails to restore) its session.
  const [firebaseUid, setFirebaseUid] = useState<string | null | undefined>(
    undefined
  );
  useEffect(
    () =>
      onAuthStateChanged(firebaseAuth, (user) => setFirebaseUid(user?.uid ?? null)),
    []
  );

  const isLoggedIn = loggedIn === LoginState.LOGGED_IN;
  const isAuthReady = isLoggedIn && typeof firebaseUid === "string";

  // Send a signed-out visitor through the normal login flow, stashing the full
  // path *and query string* so the requestId survives the round trip. Every
  // other caller of setRedirectPath stores the pathname alone, which would drop
  // it. useLogin navigates back here once the account finishes loading.
  // Keyed on LOGGED_OUT rather than appInitialized: appInitialized is only ever
  // set once a signed-in user's data loads, so it never flips for a visitor.
  useEffect(() => {
    if (isFramed) {
      return;
    }
    if (loggedIn === LoginState.LOGGED_OUT) {
      dispatch(accountActions.setRedirectPath(pathname + search));
      navigate(NavigationId.SIGN_IN, { replace: true });
    }
  }, [isFramed, loggedIn, dispatch, navigate, pathname, search]);

  // Persisted Redux can say LOGGED_IN after the Firebase session is gone (e.g.
  // revoked), and nothing else dispatches for that case. Flipping to LOGGED_OUT
  // hands off to the effect above; a fresh sign-in gives Firebase a user, so
  // this can't fire again on the way back.
  useEffect(() => {
    if (!isFramed && isLoggedIn && firebaseUid === null) {
      dispatch(accountActions.setLoggedIn(LoginState.LOGGED_OUT));
    }
  }, [isFramed, isLoggedIn, firebaseUid, dispatch]);

  useEffect(() => {
    if (isFramed || !isAuthReady) {
      return;
    }
    if (requestId === "") {
      setLoadState({
        kind: "error",
        message:
          "This link is missing its connection request. Start the connection again from the app you're connecting to Markit.",
      });
      return;
    }
    let active = true;
    setLoadState({ kind: "loading" });
    fetchAuthorizationRequest(requestId).then((result) => {
      if (!active) {
        return;
      }
      if (result.status === "ok") {
        setLoadState({ kind: "ready", request: result.request });
      } else if (result.status === "expired") {
        setLoadState({ kind: "expired" });
      } else {
        setLoadState({ kind: "error", message: result.message });
      }
    });
    return () => {
      active = false;
    };
  }, [isFramed, isAuthReady, requestId]);

  const onDecision = useCallback(
    async (decision: OAuthDecision) => {
      if (isFramed || loadState.kind !== "ready" || submittingRef.current) {
        return;
      }
      // The backend binds the Firebase user's own uid, not an account being
      // viewed through admin mode.
      if (originalAccountAdmin) {
        setSubmitError(
          "You're viewing another account in admin mode. Switch back to your own account to connect an app."
        );
        return;
      }
      submittingRef.current = true;
      setPendingDecision(decision);
      setSubmitError("");

      // A team member's Firebase user is their own account, while accountData
      // holds the team owner's.
      const expectedUid = teamMemberAccountData?.uid || accountData.uid;
      const result = await submitConsentDecision(
        requestId,
        decision,
        expectedUid
      );
      if (result.status === "ok") {
        mixpanel.track("Webapp: OAuth Consent Decision", {
          decision,
          client_name: loadState.request.clientName,
          client_domain: loadState.request.clientDomain,
        });
        if (isSafeRedirectUrl(result.redirectUrl)) {
          // A real top-level navigation: this leaves markitai.com for the
          // client's own callback URL, so it can't be a fetch or a router push.
          window.location.href = result.redirectUrl;
          return;
        }
        submittingRef.current = false;
        setPendingDecision(null);
        setSubmitError(
          "Markit couldn't safely send you back to the app. Try connecting again from the app you're connecting to Markit."
        );
        return;
      }
      submittingRef.current = false;
      setPendingDecision(null);
      if (result.status === "expired") {
        setLoadState({ kind: "expired" });
      } else if (result.status === "accountMismatch") {
        setSubmitError(ACCOUNT_MISMATCH_MESSAGE);
      } else {
        setSubmitError(result.message);
      }
    },
    [
      isFramed,
      loadState,
      originalAccountAdmin,
      teamMemberAccountData,
      accountData.uid,
      requestId,
      mixpanel,
    ]
  );

  if (isFramed) {
    return (
      <PageShell>
        <CenteredMessage
          title="Open this page in its own window"
          subtitle="For your security, this page can't be shown inside another website."
        />
      </PageShell>
    );
  }

  if (!isAuthReady) {
    return (
      <PageShell>
        <DotWave size={47} speed={1} color={Colors.BLACK} />
      </PageShell>
    );
  }

  if (loadState.kind === "loading") {
    return (
      <PageShell>
        <DotWave size={47} speed={1} color={Colors.BLACK} />
      </PageShell>
    );
  }

  if (loadState.kind === "expired") {
    return (
      <PageShell>
        <CenteredMessage
          title="This connection request has expired"
          subtitle="Connection requests are only good for a few minutes. Please try connecting again from the app you're connecting to Markit."
        />
      </PageShell>
    );
  }

  if (loadState.kind === "error") {
    return (
      <PageShell>
        <CenteredMessage
          isError
          title="We couldn't load this connection request"
          subtitle={loadState.message}
        />
      </PageShell>
    );
  }

  const { clientName, clientDomain, redirectDomain, redirectIsLoopback, scope } =
    loadState.request;
  const appName = clientName || "An app";
  const isSubmitting = pendingDecision !== null;
  // The spec requires the redirect host to be visible. Loopback gets its own
  // warning below, and it's usually the same host as clientDomain anyway.
  const showRedirectDomain =
    !redirectIsLoopback && !!redirectDomain && redirectDomain !== clientDomain;

  return (
    <PageShell>
      <StandardBorderedContainer
        containerStyles={{
          width: "100%",
          maxWidth: 480,
          padding: isDesktop ? 32 : 24,
          backgroundColor: Colors.WHITE,
        }}
      >
        <div className="ColumnNormal" style={{ gap: 24 }}>
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <span className="bodyMedium" style={{ color: Colors.BLUE5 }}>
              Connection request
            </span>
            <span style={{ fontSize: 24, fontWeight: 500 }}>
              {appName} wants to connect to your Markit account
            </span>
            <span className="bodySubtext">
              {`Connecting lets ${appName} access your Markit account on your behalf, with the same access you have when you're signed in here.`}
            </span>
          </div>

          <HorizontalDivider />

          <div className="ColumnNormal" style={{ gap: 8 }}>
            <span className="smallBodySubtext">App</span>
            <span className="bodyMedium">
              {clientDomain ? `${appName} · ${clientDomain}` : appName}
            </span>
            {showRedirectDomain ? (
              <span className="smallBodySubtext">
                Sends you back to {redirectDomain}
              </span>
            ) : null}
          </div>

          <div className="ColumnNormal" style={{ gap: 8 }}>
            <span className="smallBodySubtext">Connecting as</span>
            <span className="bodyMedium">
              {accountData.fullName || accountData.email || "Your Markit account"}
            </span>
            {accountData.fullName && accountData.email ? (
              <span className="smallBodySubtext">{accountData.email}</span>
            ) : null}
          </div>

          {scope ? (
            <div className="ColumnNormal" style={{ gap: 8 }}>
              <span className="smallBodySubtext">Requested access</span>
              <span className="bodyMedium">{scope}</span>
            </div>
          ) : null}

          {redirectIsLoopback ? (
            <span className="smallBodySubtext" style={{ color: Colors.ORANGE1 }}>
              This app will send you back to an address on your own computer.
              Only approve this if you just started the connection from an app
              on this device.
            </span>
          ) : (
            <span className="smallBodySubtext">
              Only approve this if you just started the connection from {appName}.
            </span>
          )}

          {submitError !== "" ? (
            <span className="bodySubtext" style={{ color: Colors.RED3 }}>
              {submitError}
            </span>
          ) : null}

          <div className="ColumnNormal" style={{ gap: 10 }}>
            <RectangleButton
              buttonLabel="Approve"
              onPress={() => onDecision("approve")}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              altPaddingVert={14}
              disabled={isSubmitting}
              loading={pendingDecision === "approve"}
            />
            <RectangleButton
              buttonLabel="Deny"
              onPress={() => onDecision("deny")}
              altColor={Colors.GRAY6}
              altTextColor={Colors.BLACK}
              altPaddingVert={14}
              disabled={isSubmitting}
              loading={pendingDecision === "deny"}
            />
          </div>
        </div>
      </StandardBorderedContainer>
    </PageShell>
  );
});

export default OAuthConsentScreen;
